import { z } from 'zod'
import { api } from '@/lib/api'
import { newIdempotencyKey } from '@/lib/idempotency'
import { roleSchema } from '@/features/auth/types'

/**
 * Platform-admin (THINKADMIN) endpoints. Onboarding a client creates a new
 * tenant + first facility + an invited SUPER_ADMIN.
 */

export const clientSchema = z.object({
  id: z.string(),
  name: z.string(),
  slug: z.string(),
  status: z.string(),
  primary_contact_email: z.string().nullable(),
  primary_contact_name: z.string().nullable(),
  pending_invite: z.boolean(),
  created_at: z.string().nullable(),
})
export type Client = z.infer<typeof clientSchema>

export interface CreateClientInput {
  org_name: string
  facility_name: string
  facility_address?: string
  facility_city?: string
  facility_state?: string
  facility_zip?: string
  facility_phone?: string
  admin_email: string
  admin_name: string
  /** Provide to skip invite email and activate the account immediately. */
  admin_password?: string
}

export interface CreateClientResult {
  tenant: Client
  invite_email: string
  /** Populated only in dev (SES disabled). */
  invite_url: string | null
  email_sent: boolean
}

export interface ResendResult {
  invite_email: string
  invite_url: string | null
  email_sent: boolean
}

export async function listClients(): Promise<Client[]> {
  const raw = await api.get<unknown[]>('/admin/clients')
  return raw.map((c) => clientSchema.parse(c))
}

export function createClient(input: CreateClientInput): Promise<CreateClientResult> {
  return api.post<CreateClientResult>(
    '/admin/clients',
    input as unknown as Record<string, unknown>,
    { idempotencyKey: newIdempotencyKey() },
  )
}

export function resendClientInvite(tenantId: string): Promise<ResendResult> {
  return api.post<ResendResult>(`/admin/clients/${tenantId}/resend`)
}

export interface UpdateClientInput {
  org_name?: string
  primary_contact_name?: string
  primary_contact_email?: string
}

export async function updateClient(tenantId: string, input: UpdateClientInput): Promise<Client> {
  const raw = await api.patch<unknown>(
    `/admin/clients/${tenantId}`,
    input as unknown as Record<string, unknown>,
  )
  return clientSchema.parse(raw)
}

export const clientFacilitySchema = z.object({
  id: z.string(),
  name: z.string(),
  address_line1: z.string().nullable(),
  city: z.string().nullable(),
  state: z.string().nullable(),
  postal_code: z.string().nullable(),
  phone: z.string().nullable(),
  status: z.enum(['active', 'archived']),
})
export type ClientFacility = z.infer<typeof clientFacilitySchema>

export interface AddClientFacilityInput {
  name: string
  address_line1?: string
  city?: string
  state?: string
  postal_code?: string
  phone?: string
}

export async function listClientFacilities(tenantId: string): Promise<ClientFacility[]> {
  const raw = await api.get<unknown[]>(`/admin/clients/${tenantId}/facilities`)
  return raw.map((f) => clientFacilitySchema.parse(f))
}

export async function addClientFacility(tenantId: string, input: AddClientFacilityInput): Promise<ClientFacility> {
  const raw = await api.post<unknown>(
    `/admin/clients/${tenantId}/facilities`,
    input as unknown as Record<string, unknown>,
    { idempotencyKey: newIdempotencyKey() },
  )
  return clientFacilitySchema.parse(raw)
}

export const adminFacilitySchema = z.object({
  id: z.string(),
  tenant_id: z.string(),
  tenant_name: z.string(),
  name: z.string(),
  address_line1: z.string().nullable(),
  city: z.string().nullable(),
  state: z.string().nullable(),
  postal_code: z.string().nullable(),
  phone: z.string().nullable(),
  status: z.enum(['active', 'archived']),
})
export type AdminFacility = z.infer<typeof adminFacilitySchema>

export const adminUserSchema = z.object({
  id: z.string(),
  tenant_id: z.string(),
  tenant_name: z.string(),
  facility_id: z.string().nullable(),
  facility_name: z.string().nullable(),
  name: z.string(),
  email: z.string(),
  role: roleSchema,
  status: z.string(),
  created_at: z.string().nullable(),
})
export type AdminUser = z.infer<typeof adminUserSchema>

export interface UpdateAdminFacilityInput {
  name?: string
  address_line1?: string
  city?: string
  state?: string
  postal_code?: string
  phone?: string
  status?: string
}

export async function listAllFacilities(): Promise<AdminFacility[]> {
  const raw = await api.get<unknown[]>('/admin/all-facilities')
  return raw.map((f) => adminFacilitySchema.parse(f))
}

export async function updateAdminFacility(facilityId: string, input: UpdateAdminFacilityInput): Promise<AdminFacility> {
  const raw = await api.patch<unknown>(
    `/admin/facilities/${facilityId}`,
    input as unknown as Record<string, unknown>,
  )
  return adminFacilitySchema.parse(raw)
}

export async function listAllUsers(): Promise<AdminUser[]> {
  const raw = await api.get<unknown[]>('/admin/users')
  return raw.map((u) => adminUserSchema.parse(u))
}

export interface InviteUserToClientInput {
  email: string
  name: string
  role: 'NURSE' | 'VIEWER' | 'SUPER_ADMIN'
  password?: string
  facility_id?: string
}

export const inviteUserToClientResultSchema = z.object({
  id: z.string(),
  name: z.string(),
  email: z.string(),
  role: roleSchema,
  tenant_name: z.string(),
  invite_url: z.string().nullable(),
  email_sent: z.boolean(),
})
export type InviteUserToClientResult = z.infer<typeof inviteUserToClientResultSchema>

export async function inviteUserToClient(tenantId: string, input: InviteUserToClientInput): Promise<InviteUserToClientResult> {
  const raw = await api.post<unknown>(
    `/admin/clients/${tenantId}/users/invite`,
    input as unknown as Record<string, unknown>,
    { idempotencyKey: newIdempotencyKey() },
  )
  return inviteUserToClientResultSchema.parse(raw)
}
